import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc, arrayUnion, runTransaction } from 'firebase/firestore';

const NAME_POOLS_COLLECTION = 'name_pools';

export const getNamePoolsByUserId = async (userId) => {
  try {
    const poolsRef = collection(db, NAME_POOLS_COLLECTION);
    const q = query(poolsRef, where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    
    const pools = [];
    querySnapshot.forEach((doc) => {
      pools.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return pools;
  } catch (error) {
    console.error('Error loading name pools:', error);
    throw error;
  }
};

export const createNamePool = async (userId, poolData) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!poolData.name) throw new Error('Pool name is required');

    const newPool = {
      name: poolData.name,
      userId,
      usedNames: poolData.usedNames || [],
      createdAt: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, NAME_POOLS_COLLECTION), newPool);
    
    return {
      id: docRef.id,
      ...newPool
    };
  } catch (error) {
    console.error('Error creating name pool:', error);
    throw error;
  }
};

export const updateNamePool = async (poolId, poolData) => {
  try {
    if (!poolId) throw new Error('Pool ID is required');

    const poolRef = doc(db, NAME_POOLS_COLLECTION, poolId);
    
    const updateData = {
      ...poolData,
      updatedAt: new Date().toISOString()
    };

    await updateDoc(poolRef, updateData);

    return {
      id: poolId,
      ...updateData
    };
  } catch (error) {
    console.error('Error updating name pool:', error);
    throw error;
  }
};

export const deleteNamePool = async (poolId) => {
  try {
    if (!poolId) throw new Error('Pool ID is required');
    
    const poolRef = doc(db, NAME_POOLS_COLLECTION, poolId);
    await deleteDoc(poolRef);
  } catch (error) {
    console.error('Error deleting name pool:', error);
    throw error;
  }
};

export const getNamePoolById = async (poolId) => {
  try {
    if (!poolId) return null;
    
    const poolRef = doc(db, NAME_POOLS_COLLECTION, poolId);
    const poolDoc = await getDoc(poolRef);
    
    if (poolDoc.exists()) {
      return { id: poolDoc.id, ...poolDoc.data() };
    }
    return null;
  } catch (error) {
    console.error(`Error getting name pool ${poolId}:`, error);
    return null;
  }
};

/**
 * Atomically adds a completely new name to the pool.
 * This is crucial for concurrent bulk operations to ensure a name is added
 * exactly once without race conditions.
 */
export const appendToNamePoolAtomically = async (poolId, newName) => {
  if (!poolId || !newName) return;
  
  try {
    const poolRef = doc(db, NAME_POOLS_COLLECTION, poolId);
    
    await updateDoc(poolRef, {
      usedNames: arrayUnion(newName)
    });
    
    return true;
  } catch (error) {
    console.error(`Error appending name to pool ${poolId}:`, error);
    throw error;
  }
};
